import { useState, useEffect, useRef, memo, useMemo } from 'react';
import { FigurePanel, useFigureCompositionStore, getPanelRow } from '@/store/figure-composition';
import { usePlotsStore } from '@/store/plots';
import { PanelLabel } from './PanelLabel';
import { PanelToolbar } from './PanelToolbar';
import { PlotSelector } from './PlotSelector';
import { AnnotationLayer } from './AnnotationLayer';
import Plot from 'react-plotly.js';
import type { Data, Layout } from 'plotly.js';
import { useNav } from '@/lib/nav';
import routes from '@/lib/routes';
import { useFigureEditorUiState } from '@/hooks/useFigureEditorUiState';
import {
  calculatePanelSizeMm,
  mmToPxAtDpi,
  pxToMmAtDpi,
  PREVIEW_DPI,
  BASE_FIGURE_WIDTH_MM,
  BASE_FIGURE_HEIGHT_MM,
} from '@/lib/panelScaling';
import { scaleFigureToPanel } from '@/lib/scaleFigureToPanel';
import { applyLegendLayout } from '@/lib/figureLegendLayout';
import { mergeMapboxConfig } from '@/lib/plotly-mapbox-config';

// Deep clone utility to prevent Plotly from mutating shared layout objects
function cloneDeep<T>(obj: T): T {
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }
  if (obj instanceof Date) {
    return new Date(obj.getTime()) as unknown as T;
  }
  if (obj instanceof Array) {
    return obj.map(item => cloneDeep(item)) as unknown as T;
  }
  if (typeof obj === 'object') {
    const cloned = {} as T;
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        cloned[key] = cloneDeep(obj[key]);
      }
    }
    return cloned;
  }
  return obj;
}

interface PanelProps {
  panel: FigurePanel;
  isResizing?: boolean;
  isReadOnly?: boolean; // Set to true for shared/read-only views
}

export const Panel = memo(function Panel({ panel, isResizing = false, isReadOnly = false }: PanelProps) {
  const { go } = useNav();
  // Use individual selectors - Zustand will do shallow equality checks automatically
  // This prevents re-renders when unrelated parts of composition change
  // Note: We don't subscribe to 'panels' array to avoid re-renders when it gets a new reference
  // We'll get it directly from the store when needed (e.g., for axis sharing calculations)
  const theme = useFigureCompositionStore((state) => state.currentComposition?.theme);
  const figureLegend = useFigureCompositionStore((state) => state.currentComposition?.figureLegend);
  const legendPosition = useFigureCompositionStore((state) => state.currentComposition?.legendPosition ?? 'auto');
  const axesSharing = useFigureCompositionStore((state) => state.currentComposition?.axesSharing);
  const grid = useFigureCompositionStore((state) => state.currentComposition?.grid);
  const canvasSize = useFigureCompositionStore((state) => state.currentComposition?.canvasSize);
  const projectId = useFigureCompositionStore((state) => state.currentComposition?.projectId);
  const lockedXScaleGroups = useFigureCompositionStore((state) => state.currentComposition?.lockedXScaleGroups);
  const lockedYScaleGroups = useFigureCompositionStore((state) => state.currentComposition?.lockedYScaleGroups);
  
  // Subscribe to panels array to ensure axis sharing calculations use current positions
  // We need panels in dependencies because axis sharing logic depends on panel positions
  const panels = useFigureCompositionStore((state) => state.currentComposition?.panels);
  
  // Create currentComposition object only when needed, memoized to prevent unnecessary re-creation
  // Include panels in dependencies so axis sharing calculations use current positions
  const currentComposition = useMemo(() => {
    if (!theme) return null;
    return {
      theme,
      figureLegend,
      axesSharing,
      grid,
      panels: panels || [],
      projectId,
    } as any;
  }, [theme, figureLegend, axesSharing, grid, projectId, panels]);
  
  const hydratePanel = useFigureCompositionStore((state) => state.hydratePanel);
  const updatePanel = useFigureCompositionStore((state) => state.updatePanel);
  const selectedPanelId = useFigureCompositionStore((state) => state.selectedPanelId);
  const selectedPanelIds = useFigureCompositionStore((state) => state.selectedPanelIds);
  const setSelectedPanelId = useFigureCompositionStore((state) => state.setSelectedPanelId);
  const togglePanelSelection = useFigureCompositionStore((state) => state.togglePanelSelection);
  
  // CRITICAL: Subscribe to plot's latestVersionId so panels re-hydrate when plot is saved
  // This ensures panels that follow latest automatically pick up new versions
  // Use a stable selector function to avoid issues with Zustand's internal useCallback
  // Always provide a selector function (never null) to satisfy React hooks rules
  const plotLatestVersionIdSelector = useMemo(() => {
    const plotId = panel.plotId;
    const pid = projectId;
    // Create a stable selector function that handles all cases
    return (state: any) => {
      if (!plotId || !state) return null;
      try {
        const plot = (state.currentPlot?.id === plotId ? state.currentPlot : null) ||
                     (state.plots && Array.isArray(state.plots) ? state.plots.find((p: any) => p && p.id === plotId) : null) ||
                     (pid && state.getPlotByContext ? state.getPlotByContext({ projectId: pid, id: plotId }) : null);
        return plot?.latestVersionId ?? null;
      } catch (err) {
        console.warn('[Panel] Error in plotLatestVersionId selector:', err);
        return null;
      }
    };
  }, [panel.plotId, projectId]);
  
  const plotLatestVersionId = usePlotsStore(plotLatestVersionIdSelector);
  const [showPlotSelector, setShowPlotSelector] = useState(false);
  const [loading, setLoading] = useState(false);
  const [plotlyData, setPlotlyData] = useState<{ data: Data[]; layout: Partial<Layout> } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const [panelDimensions, setPanelDimensions] = useState<{ width: number; height: number }>({ width: 0, height: 0 });
  const lastHydratedThemeRef = useRef<string>('');
  const lastHydratedThemeKeyRef = useRef<string>('');
  const lastHydrationSignatureRef = useRef<string>('');
  const isHydratingRef = useRef<boolean>(false);
  // Ref to track the latest color-correct figure - single source of truth for any manual Plotly.newPlot calls
  const latestFigureRef = useRef<{ data: any[]; layout: any } | null>(null);
  
  const isSelected = selectedPanelId === panel.id;
  const isMultiSelected = selectedPanelIds.includes(panel.id);
  const { showPanelLabels, showPanelBorders } = useFigureEditorUiState();
  
  // In shared/read-only views, always show panel labels (but non-editable)
  // In editor views, show labels based on showPanelLabels setting
  const isSharedView = '_sharedPlotData' in panel || isReadOnly;
  const shouldShowPanelLabels = isSharedView || showPanelLabels;
  
  // Debug logging for panel label visibility
  useEffect(() => {
    console.log('🔍 [Panel] Label visibility check:', {
      panelId: panel.id,
      panelLabel: panel.label,
      showPanelLabels,
      isSharedView,
      shouldShowPanelLabels,
      hasLabel: !!(panel.label && panel.label.trim())
    });
  }, [panel.id, panel.label, showPanelLabels, isSharedView, shouldShowPanelLabels]);

  // Compute isAxisLocked flag: panel is locked when it participates in standardized axes and does not override them
  const standardizedX = axesSharing?.standardizeXScales;
  const standardizedY = axesSharing?.standardizeYScales;
  const axesOverride = panel.layoutOverrides?.axesOverride ?? false;
  const isAxisLocked = (standardizedX || standardizedY) && !axesOverride;

  // Build the complete figure with all transformations applied (before scaling)
  const transformedFigure = useMemo(() => {
    if (!plotlyData) return null;
    
    // For multi-panel figures, apply legend layout:
    // - In figure-level mode: apply to the selected legend panel (figureLegend.panelId)
    // - In per-panel mode: apply to all panels (each gets its own outside-positioned legend)
    const isMultiPanel = panels && panels.length >= 2;
    const isFigureLevelMode = currentComposition?.figureLegend?.enabled === true;
    const selectedLegendPanelId = currentComposition?.figureLegend?.panelId;
    // In figure-level mode, check if this panel is the selected legend panel
    // In per-panel mode, this doesn't matter since all panels get legends
    const isSelectedLegendPanel = isFigureLevelMode 
      ? (selectedLegendPanelId ? panel.id === selectedLegendPanelId : panel.id === panels?.[0]?.id)
      : true; // In per-panel mode, all panels are "selected"
    
    // Debug: Log legend panel selection
    if (isMultiPanel && isFigureLevelMode) {
      console.log(`[Panel ${panel.id}] Legend panel check:`, {
        panelId: panel.id,
        selectedLegendPanelId,
        isSelectedLegendPanel,
        allPanelIds: panels?.map(p => p.id),
        figureLegend: currentComposition?.figureLegend
      });
    }
    
    // Determine if we should apply legend layout
    const shouldApplyLegendLayout = 
      legendPosition !== 'none' && (
        // Figure-level mode: only apply to the selected legend panel
        (isMultiPanel && isSelectedLegendPanel && isFigureLevelMode) ||
        // Per-panel mode: apply to all panels
        (isMultiPanel && !isFigureLevelMode)
      );
    
    // Apply legend layout early if needed, so we can use the updated layout
    let layoutWithLegendApplied: Partial<Layout> | null = null;
    if (shouldApplyLegendLayout) {
      const baseLayoutForLegend = plotlyData.layout || {};
      // In per-panel mode, treat as single-panel for legend positioning (each panel is independent)
      const isMultiForLegend = isFigureLevelMode ? isMultiPanel : false;
      layoutWithLegendApplied = applyLegendLayout(baseLayoutForLegend, legendPosition, isMultiForLegend);
      console.log('[Panel] Legend layout applied:', {
        panelId: panel.id,
        isSelectedLegendPanel,
        selectedLegendPanelId,
        legendPosition,
        isFigureLevelMode,
        isMultiForLegend,
        legend: layoutWithLegendApplied.legend,
        margin: layoutWithLegendApplied.margin
      });
    }
    
    const baseLayout = {
      ...plotlyData.layout,
      autosize: false,
      // Explicitly preserve coloraxis to ensure heatmap colorscales are maintained
      coloraxis: plotlyData.layout?.coloraxis,
      font: plotlyData.layout?.font || {
        family: currentComposition?.theme.fontFamily || 'Arial',
        size: Math.round(8 * (currentComposition?.theme.fontScale ?? (currentComposition?.theme.fontSize ? currentComposition.theme.fontSize / 8 : 1.0))),
      },
      margin: (() => {
        // For multi-panel figures with legend layout applied, use legend layout margins
        if (layoutWithLegendApplied) {
          return layoutWithLegendApplied.margin || plotlyData.layout?.margin;
        }
        
        // For single-panel or non-first panels, use existing logic
        return isAxisLocked
          ? plotlyData.layout.margin
          : {
              ...(plotlyData.layout?.margin ?? {}),
              ...(panel.layoutOverrides?.margin ?? {}),
            };
      })(),
      // For multi-panel figures, use new legendPosition field
      // For single-panel figures, use existing logic
      showlegend: (() => {
        // Check if figure-level legend mode is enabled
        const isFigureLevelMode = currentComposition?.figureLegend?.enabled === true;
        
        // CRITICAL: If panel.layoutOverrides.showLegend is explicitly set, respect it first
        // This allows individual panels to override legend visibility regardless of legend layout
        if (panel.layoutOverrides?.showLegend !== undefined) {
          return panel.layoutOverrides.showLegend;
        }
        
        // If legend layout was applied, use its showlegend setting
        if (layoutWithLegendApplied !== null) {
          return layoutWithLegendApplied.showlegend ?? true;
        }
        
        if (isMultiPanel && isFigureLevelMode) {
          // Multi-panel + figure-level mode: show legend only on the selected legend panel
          const selectedLegendPanelId = currentComposition?.figureLegend?.panelId;
          const isSelectedLegendPanel = selectedLegendPanelId 
            ? panel.id === selectedLegendPanelId 
            : panel.id === panels?.[0]?.id; // Fallback to first panel if no panelId set
          
          if (isSelectedLegendPanel) {
            return legendPosition !== 'none';
          } else {
            // Hide legend on other panels
            return false;
          }
        }
        
        // Per-panel mode or single-panel: use existing logic
        if (currentComposition?.figureLegend?.enabled) {
          return currentComposition.figureLegend.panelId === panel.id;
        }
        return plotlyData.layout?.showlegend;
      })(),
      legend: (() => {
        let finalLegend: any;
        
        // If legend layout was applied (figure-level mode first panel, or per-panel mode), use it
        if (layoutWithLegendApplied && layoutWithLegendApplied.legend) {
          finalLegend = layoutWithLegendApplied.legend;
        } else {
          // For per-panel mode with legacy panel-level legend position, use existing logic
          const baseLegend = plotlyData.layout?.legend || {};
          const panelLegendPosition = panel.layoutOverrides?.legendPosition;
          
          if (!panelLegendPosition || panelLegendPosition === 'none') {
            finalLegend = baseLegend;
          } else {
            // Map position strings to Plotly legend coordinates (legacy support)
            const positionMap: Record<string, { x: number; y: number; xanchor: string; yanchor: string }> = {
              'top-right': { x: 1, y: 1, xanchor: 'right', yanchor: 'top' },
              'top-left': { x: 0, y: 1, xanchor: 'left', yanchor: 'top' },
              'bottom-right': { x: 1, y: 0, xanchor: 'right', yanchor: 'bottom' },
              'bottom-left': { x: 0, y: 0, xanchor: 'left', yanchor: 'bottom' },
            };
            
            const position = positionMap[panelLegendPosition];
            if (position) {
              finalLegend = {
                ...baseLegend,
                ...position,
              };
            } else {
              finalLegend = baseLegend;
            }
          }
        }
        
        // Apply fine-tune offsets for panel preview only
        // These offsets are NOT applied to the combined multi-panel figure
        const offsetX = panel.layoutOverrides?.legendOffsetX ?? 0;
        const offsetY = panel.layoutOverrides?.legendOffsetY ?? 0;
        
        if ((offsetX !== 0 || offsetY !== 0) && finalLegend) {
          const updatedLegend: any = { ...finalLegend };
          
          // Apply horizontal offset
          if (offsetX !== 0 && typeof finalLegend.x === 'number') {
            const baseX = finalLegend.x;
            updatedLegend.x = Math.min(1, Math.max(0, baseX + offsetX));
          }
          
          // Apply vertical offset
          if (offsetY !== 0 && typeof finalLegend.y === 'number') {
            const baseY = finalLegend.y;
            // For y position, we allow values outside [0,1] for outside positioning
            // So we don't clamp as strictly, but still apply reasonable bounds
            updatedLegend.y = baseY + offsetY;
          }
          
          finalLegend = updatedLegend;
        }
        
        return finalLegend;
      })(),
      title: (() => {
        const originalTitle = plotlyData.layout?.title;
        if (!originalTitle) return undefined;
        
        // Handle hideSubtitle first (before hideTitle check)
        // This ensures subtitle is removed even if hideTitle is also true
        let titleToReturn = originalTitle;
        if (panel.layoutOverrides?.hideSubtitle === true && originalTitle.subtitle) {
          const { subtitle, ...titleWithoutSubtitle } = originalTitle;
          titleToReturn = titleWithoutSubtitle;
        }
        
        // If hideTitle is true, hide the main title text
        // Keep the structure but make it invisible by setting font color to transparent
        // This preserves the exact layout structure so subtitle position doesn't shift
        if (panel.layoutOverrides?.hideTitle === true) {
          if (titleToReturn.subtitle) {
            // Has subtitle: preserve all properties but make title text invisible
            const titleFont = titleToReturn.font || {};
            return {
              ...titleToReturn,
              font: {
                ...titleFont,
                color: 'rgba(0,0,0,0)', // Transparent color to hide text while preserving layout
              },
            };
          }
          // No subtitle: can return undefined since there's nothing to preserve
          return undefined;
        }
        
        return titleToReturn;
      })(),
      // Hide annotations and shapes if hideAnnotations is enabled
      // Use undefined instead of empty array to ensure Plotly doesn't render them
      annotations: panel.layoutOverrides?.hideAnnotations === true 
        ? undefined 
        : plotlyData.layout?.annotations,
      shapes: panel.layoutOverrides?.hideAnnotations === true 
        ? undefined 
        : plotlyData.layout?.shapes,
    };

    // Apply zoom and pan by modifying axis ranges (only when not locked)
    // When isAxisLocked is true, do not modify axis ranges at all – use the standardized ranges from hydratePanel
    if (!isAxisLocked) {
      const zoom = panel.layoutOverrides?.zoom ?? 1.0;
      const panX = panel.layoutOverrides?.panX ?? 0.0;
      const panY = panel.layoutOverrides?.panY ?? 0.0;

      if (zoom !== 1.0 || panX !== 0.0 || panY !== 0.0) {
      const getOriginalRange = (_axis: any, isXAxis: boolean): [number, number] | null => {
        const originalAxis = isXAxis 
          ? plotlyData.layout?.xaxis 
          : plotlyData.layout?.yaxis;
        
        if (originalAxis?.range && Array.isArray(originalAxis.range) && originalAxis.range.length === 2) {
          return [originalAxis.range[0], originalAxis.range[1]];
        }
        
        const dataKey = isXAxis ? 'x' : 'y';
        const traces = plotlyData.data || [];
        let min: number | null = null;
        let max: number | null = null;
        
        for (const trace of traces) {
          const values = (trace as any)[dataKey];
          if (Array.isArray(values) && values.length > 0) {
            const numericValues = values.filter((v: any) => typeof v === 'number' && !isNaN(v));
            if (numericValues.length > 0) {
              const traceMin = Math.min(...numericValues);
              const traceMax = Math.max(...numericValues);
              if (min === null || traceMin < min) min = traceMin;
              if (max === null || traceMax > max) max = traceMax;
            }
          }
        }
        
        if (min !== null && max !== null) {
          return [min, max];
        }
        
        return null;
      };

      const xaxis = baseLayout.xaxis || {};
      const originalXRange = getOriginalRange(xaxis, true);
      if (originalXRange) {
        const [xMin, xMax] = originalXRange;
        const xRange = xMax - xMin;
        const xCenter = (xMin + xMax) / 2;
        const zoomedXRange = xRange / zoom;
        const newXMin = xCenter - zoomedXRange / 2;
        const newXMax = xCenter + zoomedXRange / 2;
        const panOffset = panX * zoomedXRange;
        const finalXMin = newXMin + panOffset;
        const finalXMax = newXMax + panOffset;
        
        baseLayout.xaxis = {
          ...xaxis,
          range: [finalXMin, finalXMax],
          autorange: false,
        };
      }

      const yaxis = baseLayout.yaxis || {};
      const originalYRange = getOriginalRange(yaxis, false);
      if (originalYRange) {
        const [yMin, yMax] = originalYRange;
        const yRange = yMax - yMin;
        const yCenter = (yMin + yMax) / 2;
        const zoomedYRange = yRange / zoom;
        const newYMin = yCenter - zoomedYRange / 2;
        const newYMax = yCenter + zoomedYRange / 2;
        const panOffset = panY * zoomedYRange;
        const finalYMin = newYMin + panOffset;
        const finalYMax = newYMax + panOffset;
        
        baseLayout.yaxis = {
          ...yaxis,
          range: [finalYMin, finalYMax],
          autorange: false,
        };
      }
      }
    }

    // Apply axes sharing visual adjustments
    const axesSharing = currentComposition?.axesSharing;
    if (axesSharing && currentComposition?.panels && currentComposition.panels.length > 0) {
      const gridUnitWidth = currentComposition.grid.cols / 12;
      const panelCol = Math.floor(panel.x / gridUnitWidth);
      const panelRow = getPanelRow(panel);

      if (axesSharing.shareXAcrossColumns || axesSharing.shareXAcrossAll) {
        const panelsArray = currentComposition.panels as FigurePanel[];
        
        if (axesSharing.shareXAcrossAll) {
          const rowPositions = panelsArray.map((p) => getPanelRow(p));
          if (rowPositions.length > 0) {
            const maxRow = Math.max(...rowPositions);
            const isBottomRow = panelRow === maxRow;
            if (!isBottomRow && baseLayout.xaxis) {
              (baseLayout.xaxis as any).showticklabels = false;
              (baseLayout.xaxis as any).title = undefined;
            }
          }
        } else if (axesSharing.shareXAcrossColumns) {
          const columnPanels = panelsArray.filter(
            (p) => Math.floor(p.x / gridUnitWidth) === panelCol
          );
          const columnRowPositions = columnPanels.map((p) => getPanelRow(p));
          if (columnRowPositions.length > 0) {
            const maxRowInColumn = Math.max(...columnRowPositions);
            const isBottomRowInColumn = panelRow === maxRowInColumn;
            if (!isBottomRowInColumn && baseLayout.xaxis) {
              (baseLayout.xaxis as any).showticklabels = false;
              (baseLayout.xaxis as any).title = undefined;
            }
          }
        }
      }

      if (axesSharing.shareYAcrossRows) {
        const panelsArray = currentComposition.panels as FigurePanel[];
        const colPositions = panelsArray.map((p) => Math.floor(p.x / gridUnitWidth));
        if (colPositions.length > 0) {
          const minCol = Math.min(...colPositions);
          const isLeftColumn = panelCol === minCol;
          if (!isLeftColumn && baseLayout.yaxis) {
            (baseLayout.yaxis as any).showticklabels = false;
            (baseLayout.yaxis as any).title = undefined;
          }
        }
      }
    }

    // Ensure coloraxis is preserved in final layout
    const finalLayout = {
      ...baseLayout,
      coloraxis: baseLayout.coloraxis ?? plotlyData.layout?.coloraxis,
    };
    
    // DEBUG: Log coloraxis preservation through transformation
    console.log(`🔍 [Panel] DEBUG - transformedFigure for panel ${panel.id}:`, {
      plotlyDataColoraxis: (plotlyData.layout?.coloraxis as any)?.colorscale,
      baseLayoutColoraxis: (baseLayout.coloraxis as any)?.colorscale,
      finalLayoutColoraxis: (finalLayout.coloraxis as any)?.colorscale,
      coloraxisExists: !!finalLayout.coloraxis,
    });
    
    return {
      data: plotlyData.data,
      layout: finalLayout,
    };
  }, [
    plotlyData,
    currentComposition?.theme.fontFamily,
    currentComposition?.theme.fontScale,
    currentComposition?.theme.fontSize,
    currentComposition?.figureLegend,
    legendPosition,
    panels,
    panel.layoutOverrides,
    currentComposition?.axesSharing,
    panel.id,
    panel.x,
    isAxisLocked,
    panel.layoutOverrides?.axesOverride,
  ]);

  // Calculate panel size in mm (use stored values or calculate from grid units)
  // During resize, use actual DOM dimensions converted to mm
  const panelSizeMm = useMemo(() => {
    // During resize, use actual DOM size converted to mm
    if (isResizing && panelDimensions.width > 0 && panelDimensions.height > 0) {
      const widthMm = pxToMmAtDpi(panelDimensions.width, PREVIEW_DPI);
      const heightMm = pxToMmAtDpi(panelDimensions.height, PREVIEW_DPI);
      return { widthMm, heightMm };
    }
    
    // Use stored mm dimensions if available
    if (panel.panelWidthMm && panel.panelHeightMm) {
      return { widthMm: panel.panelWidthMm, heightMm: panel.panelHeightMm };
    }
    
    // Calculate from grid units if mm dimensions not set
    if (grid && canvasSize) {
      return calculatePanelSizeMm(
        panel.w,
        panel.h,
        canvasSize.width,
        canvasSize.height,
        grid.cols,
        grid.rowHeight,
        grid.margin,
        PREVIEW_DPI
      );
    }
    
    // Fallback to base size
    return { widthMm: BASE_FIGURE_WIDTH_MM, heightMm: BASE_FIGURE_HEIGHT_MM };
  }, [isResizing, panelDimensions.width, panelDimensions.height, panel.panelWidthMm, panel.panelHeightMm, panel.w, panel.h, grid, canvasSize]);

  // Calculate panel size in pixels for rendering
  // Only use canonical dimensions (640×320) when axis is actually locked (standardized AND not overridden)
  // Panels that opt out via axesOverride should size to their actual cell dimensions
  // Get canonical dimensions from normalized layout (set by normalizePanelGeometryForSharedAxis)
  // These are: width=640, height=320 (from normalizePanelGeometryForSharedAxis)
  const normalizedLayoutWidth = plotlyData?.layout?.width as number | undefined;
  const normalizedLayoutHeight = plotlyData?.layout?.height as number | undefined;
  
  // Use normalized layout dimensions only when axis is locked, otherwise use measured/calculated dimensions
  // Memoize to recalculate when plotlyData changes (after hydration sets normalized dimensions)
  const panelWidthPx = useMemo(() => {
    // Only use canonical width when axis is locked (standardized AND not overridden)
    if (isAxisLocked && normalizedLayoutWidth) {
      return normalizedLayoutWidth; // Use normalized layout width (640px) for consistent scaling
    }
    // For override panels or non-standardized panels, use actual cell dimensions
    if (panelDimensions.width > 0) {
      return panelDimensions.width;
    }
    return mmToPxAtDpi(panelSizeMm.widthMm, PREVIEW_DPI);
  }, [isAxisLocked, normalizedLayoutWidth, panelDimensions.width, panelSizeMm.widthMm]);
    
  const panelHeightPx = useMemo(() => {
    // Only use canonical height when axis is locked (standardized AND not overridden)
    if (isAxisLocked && normalizedLayoutHeight) {
      return normalizedLayoutHeight; // Use normalized layout height (320px) for consistent scaling
    }
    // For override panels or non-standardized panels, use actual cell dimensions
    if (panelDimensions.height > 0) {
      return panelDimensions.height;
    }
    return mmToPxAtDpi(panelSizeMm.heightMm, PREVIEW_DPI);
  }, [isAxisLocked, normalizedLayoutHeight, panelDimensions.height, panelSizeMm.heightMm]);

  // Look up group scale factors based on panel's column/row position
  const colKey = `col:${panel.x}`;
  const rowKey = `row:${panel.y}`;
  const xGroupScale = axesSharing?.standardizeXScales
    ? lockedXScaleGroups?.[colKey]?.scaleFactor
    : undefined;
  const yGroupScale = axesSharing?.standardizeYScales
    ? lockedYScaleGroups?.[rowKey]?.scaleFactor
    : undefined;

  // Determine lockedScaleFactorForPanel from group-based scale factors
  // This ensures panels in the same column share X-scale, panels in the same row share Y-scale
  const lockedScaleFactorForPanel = useMemo(() => {
    const axesOverride = panel.layoutOverrides?.axesOverride ?? false;
    if (axesOverride) return null;

    const shareX = axesSharing?.standardizeXScales;
    const shareY = axesSharing?.standardizeYScales;

    // No sharing active → no locked factor
    if (!shareX && !shareY) return null;

    // Only Share X
    if (shareX && !shareY) {
      return xGroupScale ?? null;
    }

    // Only Share Y
    if (!shareX && shareY) {
      return yGroupScale ?? null;
    }

    // Both Share X and Share Y:
    // For now, choose the smaller factor to avoid cropping.
    if (xGroupScale && yGroupScale) {
      return Math.min(xGroupScale, yGroupScale);
    }

    // Fallback if only one exists
    return xGroupScale ?? yGroupScale ?? null;
  }, [axesSharing, xGroupScale, yGroupScale, panel.layoutOverrides, panel.x, panel.y]);

  // Compute freehand scale factor for unlocked panels (proportional scaling based on container size)
  // Used when standardization is OFF or panel has axesOverride
  const freehandScaleFactor = useMemo(() => {
    // If axes are standardized, we don't use freehand
    const shareX = axesSharing?.standardizeXScales;
    const shareY = axesSharing?.standardizeYScales;
    if (shareX || shareY) return null;

    if (!normalizedLayoutWidth || !normalizedLayoutHeight) {
      return 1; // Fallback if normalized dimensions not available
    }
    
    // Use actual container dimensions if available, otherwise use calculated dimensions
    const containerWidth = panelDimensions.width > 0 ? panelDimensions.width : panelWidthPx;
    const containerHeight = panelDimensions.height > 0 ? panelDimensions.height : panelHeightPx;
    
    if (containerWidth <= 0 || containerHeight <= 0) {
      return 1; // Fallback if dimensions invalid
    }
    
    // Compute scale factor based on canonical dimensions (640×320)
    const scaleX = containerWidth / normalizedLayoutWidth;
    const scaleY = containerHeight / normalizedLayoutHeight;
    const scale = Math.min(scaleX, scaleY); // Maintain aspect ratio
    
    return scale;
  }, [axesSharing?.standardizeXScales, axesSharing?.standardizeYScales, normalizedLayoutWidth, normalizedLayoutHeight, panelDimensions.width, panelDimensions.height, panelWidthPx, panelHeightPx]);

  // Scale the figure to panel size (after panelWidthPx/panelHeightPx are calculated)
  // For locked panels: use per-column/per-row group scale factors to ensure panels in same column/row scale identically
  //   This ensures pixel-perfect tick alignment across panels in the same column (X) or row (Y)
  // For unlocked panels: use freehandScaleFactor computed from container size for proportional scaling
  const scaledFigure = useMemo(() => {
    if (!transformedFigure) return null;
    
    // Start from normalized layout/data (canonical figure)
    const normalizedLayout = transformedFigure.layout;
    const normalizedData = transformedFigure.data;
    
    // When axes are locked: use per-column/per-row group scale factor
    // Panels in the same column share X-scale, panels in the same row share Y-scale
    if (lockedScaleFactorForPanel && lockedScaleFactorForPanel > 0 && normalizedLayoutWidth && normalizedLayoutHeight) {
      // Scale canonical figure by the group-based locked scale factor
      const scaledWidth = Math.round(normalizedLayoutWidth * lockedScaleFactorForPanel);
      const scaledHeight = Math.round(normalizedLayoutHeight * lockedScaleFactorForPanel);
      
      // Scale canonical figure using the group-based locked scale factor
      // Panels in the same column/row will have identical scaled dimensions, maintaining perfect alignment
      return scaleFigureToPanel({ layout: normalizedLayout, data: normalizedData }, scaledWidth, scaledHeight);
    }
    
    // For unlocked panels: use freehand scale factor for proportional scaling
    // This allows each panel to scale independently based on its container size
    if (freehandScaleFactor !== null && freehandScaleFactor !== undefined && freehandScaleFactor !== 1 && normalizedLayoutWidth && normalizedLayoutHeight) {
      const scaledWidth = Math.round(normalizedLayoutWidth * freehandScaleFactor);
      const scaledHeight = Math.round(normalizedLayoutHeight * freehandScaleFactor);
      return scaleFigureToPanel({ layout: normalizedLayout, data: normalizedData }, scaledWidth, scaledHeight);
    }
    
    // Fallback: use normalized figure without additional scaling
    // This happens when scale factors are not available or equal to 1
    if (panelWidthPx <= 0 || panelHeightPx <= 0) return null;
    return scaleFigureToPanel({ layout: normalizedLayout, data: normalizedData }, panelWidthPx, panelHeightPx);
  }, [transformedFigure, panelWidthPx, panelHeightPx, lockedScaleFactorForPanel, freehandScaleFactor, normalizedLayoutWidth, normalizedLayoutHeight]);

  // Clone layout and data before rendering to prevent Plotly from mutating shared objects
  // Plotly mutates layout objects in place (injects _fullLayout, adjusts margin.l, etc.)
  // Without cloning, one panel's mutations bleed into others, causing tick misalignment
  const renderedFigure = useMemo(() => {
    const source = scaledFigure ?? transformedFigure;
    if (!source) return null;
    
    // Deep clone layout to prevent Plotly from mutating the canonical geometry
    const layoutClone = cloneDeep(source.layout);
    
    // Ensure coloraxis is preserved after cloning
    if (source.layout?.coloraxis && !layoutClone.coloraxis) {
      layoutClone.coloraxis = source.layout.coloraxis;
    }
    
    // Shallow clone data array and each trace to prevent mutations
    const dataClone = source.data.map(trace => ({ ...trace }));
    
    // CRITICAL FIX: In Figure Editor context, skip theme-based colorscale adjustments
    // The figure editor should preserve original figure colors from R/Python/Notebook JSON
    // Do not adjust marker.colorscale, trace.colorscale, or layout.coloraxis.colorscale based on theme
    // Only respect the colors that arrive from latestFigure / plotlyJson (what the backend or mutation router has set)
    //
    // Panel.tsx is only used in figure editor context, so we can skip all theme-based color logic
    // The colors from hydratePanel (which preserves original figure colors) are authoritative
    //
    // Note: We still ensure coloraxis is preserved in the layout clone, but we don't modify
    // trace-level colorscales based on theme or layout coloraxis
    if (layoutClone.coloraxis) {
      // In figure editor, we trust the incoming colors as-is
      // No need to reconcile layout coloraxis with trace colorscales
      // The hydratePanel function has already preserved the original figure colors
      console.log(`🔍 [Panel] Figure editor panel ${panel.id}: preserving original figure colors (skipping theme-based colorscale adjustments)`);
    }
    
    // DEBUG: Log coloraxis preservation before rendering
    const tracesWithColoraxis = dataClone.filter((t: any) => t.coloraxis || t.marker?.coloraxis).length;
    console.log(`🔍 [Panel] DEBUG - renderedFigure for panel ${panel.id}:`, {
      sourceColoraxis: (source.layout?.coloraxis as any)?.colorscale,
      clonedColoraxis: (layoutClone.coloraxis as any)?.colorscale,
      coloraxisExists: !!layoutClone.coloraxis,
      tracesWithColoraxis: tracesWithColoraxis,
      tracesWithTraceColoraxis: dataClone.filter((t: any) => t.coloraxis).length,
      tracesWithMarkerColoraxis: dataClone.filter((t: any) => t.marker?.coloraxis).length,
      traceTypes: dataClone.map((t: any) => t.type),
      traceBindings: dataClone.map((t: any) => ({
        type: t.type,
        traceColoraxis: t.coloraxis,
        markerColoraxis: t.marker?.coloraxis,
      })),
    });
    
    return { data: dataClone, layout: layoutClone };
  }, [scaledFigure, transformedFigure, panel.id]);

  // Keep latestFigureRef in sync with renderedFigure - this is our single source of truth for color-correct figures
  useEffect(() => {
    if (renderedFigure) {
      latestFigureRef.current = {
        data: renderedFigure.data,
        layout: renderedFigure.layout,
      };
      
      // Log the colorscale we're tracking
      const colorscale = renderedFigure.layout?.coloraxis?.colorscale ??
        (renderedFigure.layout?.coloraxis && Array.isArray(renderedFigure.layout.coloraxis.colorscale)
          ? '[array]'
          : undefined);
      
      console.debug('📌 [Panel] Updated latestFigureRef for panel', panel.id, {
        coloraxisExists: !!renderedFigure.layout?.coloraxis,
        colorscale,
        traceCount: renderedFigure.data?.length ?? 0,
      });
    }
  }, [renderedFigure, panel.id]);

  // Create a stable theme key to detect changes
  // When journal template is set, only include overrides in cache key (not template defaults)
  // Use theme selector directly instead of currentComposition to prevent unnecessary re-renders
  const themeKey = useMemo(() => {
    if (!theme) return '';
    const hasJournalTemplate = !!theme.journalTemplate;
    
    if (hasJournalTemplate) {
      // Journal template sets defaults, Global Style overrides fonts/scale
      // Line width comes from journal template, not theme override
      const fontScale = theme.fontScale ?? (theme.fontSize ? theme.fontSize / 8 : 1.0);
      const overrideKey = theme.fontFamily || fontScale !== 1.0
        ? `:override:${theme.fontFamily || ''}:${fontScale.toFixed(2)}`
        : '';
      return JSON.stringify({
        journalTemplate: theme.journalTemplate,
        overrideKey,
        colorMode: theme.colorMode || 'RGB',
        colorblindSafe: theme.colorblindSafe || false,
      });
    } else {
      // No journal template: Global Style is the only source, line width defaults to 1
      const fontScale = theme.fontScale ?? (theme.fontSize ? theme.fontSize / 8 : 1.0);
      return JSON.stringify({
        journalTemplate: 'none',
        fontFamily: theme.fontFamily || 'Arial',
        fontScale: fontScale.toFixed(2),
        // lineWidth not included - comes from journal template (defaults to 1)
        colorMode: theme.colorMode || 'RGB',
        colorblindSafe: theme.colorblindSafe || false,
      });
    }
  }, [
    theme?.journalTemplate,
    theme?.fontFamily,
    theme?.fontScale,
    theme?.fontSize, // Keep for backward compatibility
    // lineWidth removed - comes from journal template, not theme
    theme?.colorMode,
    theme?.colorblindSafe,
  ]);

  // Measure panel dimensions for annotation layer and real-time resize tracking
  useEffect(() => {
    const measurePanel = () => {
      if (panelRef.current) {
        setPanelDimensions({
          width: panelRef.current.clientWidth,
          height: panelRef.current.clientHeight,
        });
      }
    };

    measurePanel();
    const resizeObserver = new ResizeObserver(measurePanel);
    if (panelRef.current) {
      resizeObserver.observe(panelRef.current);
    }

    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    let cancelled = false;

    // Check if plot data is embedded (for shared figures) - CHECK THIS FIRST before any store lookups
    if ('_sharedPlotData' in panel) {
      const sharedData = (panel as any)._sharedPlotData;
      if (sharedData && sharedData.data && Array.isArray(sharedData.data)) {
        console.log(`🎨 [Panel] Using embedded plot data for shared figure panel ${panel.id}`);
        // Apply hide flags to shared plot data layout before setting it
        // This ensures titles/subtitles/annotations are hidden according to layoutOverrides
        const layoutWithHideFlags = (() => {
          const layout = { ...(sharedData.layout || {}) };
          
          // Apply hideTitle and hideSubtitle flags
          if (layout.title) {
            const originalTitle = layout.title;
            
            // Handle hideSubtitle first (before hideTitle check)
            let titleToReturn = originalTitle;
            if (panel.layoutOverrides?.hideSubtitle === true && originalTitle.subtitle) {
              const { subtitle, ...titleWithoutSubtitle } = originalTitle;
              titleToReturn = titleWithoutSubtitle;
            }
            
            // If hideTitle is true, hide the main title text
            if (panel.layoutOverrides?.hideTitle === true) {
              if (titleToReturn.subtitle) {
                // Has subtitle: preserve all properties but make title text invisible
                const titleFont = titleToReturn.font || {};
                layout.title = {
                  ...titleToReturn,
                  font: {
                    ...titleFont,
                    color: 'rgba(0,0,0,0)', // Transparent color to hide text while preserving layout
                  },
                };
              } else {
                // No subtitle: can return undefined since there's nothing to preserve
                layout.title = undefined;
              }
            } else {
              layout.title = titleToReturn;
            }
          }
          
          // Hide annotations and shapes if hideAnnotations is enabled
          if (panel.layoutOverrides?.hideAnnotations === true) {
            layout.annotations = undefined;
            layout.shapes = undefined;
          }
          
          return layout;
        })();
        
        // Use embedded data with hide flags applied
        setPlotlyData({
          data: sharedData.data || [],
          layout: layoutWithHideFlags
        });
        setLoading(false);
        setError(null);
        isHydratingRef.current = false;
        // Set refs to prevent re-hydration attempts
        lastHydratedThemeRef.current = `shared:${panel.id}`;
        lastHydratedThemeKeyRef.current = 'shared';
        lastHydrationSignatureRef.current = 'shared';
        return () => {
          cancelled = true;
        };
      } else {
        console.warn(`🎨 [Panel] Panel ${panel.id} has _sharedPlotData but it's null or invalid:`, sharedData);
        setError('Plot data not available in shared figure');
        setLoading(false);
        return () => {
          cancelled = true;
        };
      }
    }

    if (!panel.plotId) {
      setPlotlyData(null);
      setError(null);
      setLoading(false);
      lastHydratedThemeRef.current = '';
      lastHydratedThemeKeyRef.current = '';
      lastHydrationSignatureRef.current = '';
      isHydratingRef.current = false;
      return () => {
        cancelled = true;
      };
    }

    // Get the latest composition from the store to avoid stale closures
    const latestComposition = useFigureCompositionStore.getState().currentComposition;
    if (!latestComposition || !latestComposition.theme) {
      // Always return a cleanup function to maintain hook order consistency
      return () => {
        cancelled = true;
      };
    }

    // CRITICAL: Compute theme key directly from latest composition to avoid stale closures
    // This ensures we're comparing the actual current theme, not a memoized value
    const currentTheme = latestComposition.theme;
    const hasJournalTemplate = !!currentTheme.journalTemplate;
    
    let actualThemeKey: string;
    if (hasJournalTemplate) {
      // When journal template is set: include font/fontScale overrides
      // Line width comes from journal template, not theme override
      const fontScale = currentTheme.fontScale ?? (currentTheme.fontSize ? currentTheme.fontSize / 8 : 1.0);
      const overrideKey = currentTheme.fontFamily || fontScale !== 1.0
        ? `:override:${currentTheme.fontFamily || ''}:${fontScale.toFixed(2)}`
        : '';
      actualThemeKey = JSON.stringify({
        journalTemplate: currentTheme.journalTemplate,
        overrideKey,
        colorMode: currentTheme.colorMode || 'RGB',
        colorblindSafe: currentTheme.colorblindSafe || false,
      });
    } else {
      // No journal template: line width defaults to 1 (from journal template logic)
      const fontScale = currentTheme.fontScale ?? (currentTheme.fontSize ? currentTheme.fontSize / 8 : 1.0);
      actualThemeKey = JSON.stringify({
        journalTemplate: 'none',
        fontFamily: currentTheme.fontFamily || 'Arial',
        fontScale: fontScale.toFixed(2),
        // lineWidth not included - comes from journal template (defaults to 1)
        colorMode: currentTheme.colorMode || 'RGB',
        colorblindSafe: currentTheme.colorblindSafe || false,
      });
    }

    // CRITICAL: Resolve effectiveVersionId synchronously for use in signature
    // This ensures follow-latest panels use the actual version ID, not null
    const { resolveEffectivePlotVersionSync, hasPanelCachedData } = useFigureCompositionStore.getState();
    const resolvedVersion = resolveEffectivePlotVersionSync({
      plotId: panel.plotId,
      panelVersionId: panel.plotVersionId,
      projectId: latestComposition?.projectId,
    });
    const effectiveVersionId = resolvedVersion.versionId;
    
    // effectiveVersionId will be either:
    // - A real versionId (UUID) if panel is pinned
    // - 'latest' (string) if panel follows latest (hydratePanel will resolve this to actual versionId)
    // - null should not happen with the new pure implementation, but handle it gracefully
    if (!effectiveVersionId) {
      console.warn(`[Panel] Panel ${panel.id}: No effectiveVersionId, will attempt resolution in hydratePanel`);
    }
    
    // Create hydration signature that includes theme, axes sharing, and panel overrides
    // CRITICAL: Do NOT include peer panel positions - layout changes should not trigger re-hydration
    // Axis sharing behavior is handled by hydratePanel internally, not by signature
    const axesSharingSignature = axesSharing ? JSON.stringify({
      shareXAcrossColumns: axesSharing.shareXAcrossColumns,
      shareXAcrossAll: axesSharing.shareXAcrossAll,
      shareYAcrossRows: axesSharing.shareYAcrossRows,
      standardizeXTitles: axesSharing.standardizeXTitles,
      standardizeYTitles: axesSharing.standardizeYTitles,
      standardizeXScales: axesSharing.standardizeXScales,
      standardizeYScales: axesSharing.standardizeYScales,
    }) : 'no-sharing';
    
    const overridesSignature = panel.layoutOverrides ? JSON.stringify({
      margin: panel.layoutOverrides.margin,
      showLegend: panel.layoutOverrides.showLegend,
      axesOverride: panel.layoutOverrides.axesOverride,
      zoom: panel.layoutOverrides.zoom,
      panX: panel.layoutOverrides.panX,
      panY: panel.layoutOverrides.panY,
      hideTitle: panel.layoutOverrides.hideTitle,
      hideSubtitle: panel.layoutOverrides.hideSubtitle,
      hideAnnotations: panel.layoutOverrides.hideAnnotations,
    }) : 'no-overrides';
    
    // CRITICAL: Signature does NOT include peer panel positions
    // Moving/adding panels should not trigger re-hydration unless plot/version/theme/overrides change
    const hydrationSignature = `${actualThemeKey}:${axesSharingSignature}:${overridesSignature}`;
    
    // CRITICAL: Check if we already have cached data for this (plotId, effectiveVersionId, theme, overrides) combo
    // Use store cache check instead of local plotlyData state to avoid hydration loops
    // Note: If effectiveVersionId is 'latest', we can't check cache yet (hydratePanel will resolve it first)
    const hasCachedData = effectiveVersionId && effectiveVersionId !== 'latest' ? hasPanelCachedData({
      plotId: panel.plotId,
      effectiveVersionId,
      themeKey: actualThemeKey,
      axesSharingKey: axesSharingSignature,
      overridesKey: overridesSignature,
    }) : false;
    
    // If we have cached data AND local state AND signature unchanged, skip hydration
    // But if plotlyData is null, we still need to call hydratePanel to populate it from cache
    if (hasCachedData && plotlyData && lastHydrationSignatureRef.current === hydrationSignature) {
      console.log(`🎨 [Panel] Hydration signature unchanged and has cached data, skipping hydration for panel ${panel.id}`);
      return;
    }
    
    // If signature changed but we have cached data, hydratePanel will use cache efficiently
    // This is fine - we still call hydratePanel to ensure plotlyData is updated if needed
    
    // If we don't have effectiveVersionId yet, we can't check cache - proceed to hydratePanel which will resolve it
    if (!effectiveVersionId) {
      console.log(`🎨 [Panel] Panel ${panel.id}: No effectiveVersionId yet, proceeding to hydratePanel for async resolution`);
    }
    
    // Check if hydration signature changed
    if (lastHydrationSignatureRef.current !== '' && lastHydrationSignatureRef.current !== hydrationSignature) {
      console.log(`🎨 [Panel] Hydration signature changed for panel ${panel.id}:`, {
        previous: lastHydrationSignatureRef.current,
        current: hydrationSignature,
      });
    }
    
    // Build hydration key using effectiveVersionId (not null)
    const hydrationKey = effectiveVersionId 
      ? `${panel.plotId}:${effectiveVersionId}:${hydrationSignature}`
      : `${panel.plotId}:pending-resolution:${hydrationSignature}`;
    
    // Prevent duplicate hydrations with same key (only if we have both cached data and local state)
    if (lastHydratedThemeRef.current === hydrationKey && hasCachedData && plotlyData) {
      console.log(`🎨 [Panel] Skipping duplicate hydration for panel ${panel.id} (hydration key matches and has data)`);
      return;
    }
    
    // Prevent starting a new hydration if one is already in progress with the same signature
    // Only skip if we have both cached data and local state
    if (isHydratingRef.current && lastHydrationSignatureRef.current === hydrationSignature) {
      if (hasCachedData && plotlyData) {
        console.log(`🎨 [Panel] Hydration already in progress with same signature, skipping for panel ${panel.id}`);
        return;
      }
      // If we don't have data yet (even if cached), allow hydration to proceed to populate plotlyData
    }

    // CRITICAL FIX: Only set loading to true if we don't have plotlyData yet
    // If we have cached data, hydratePanel will return immediately without loading
    // This prevents clearing existing plots when signature changes don't affect plot data
    if (!plotlyData && !hasCachedData) {
      setLoading(true);
    }
    setError(null);

    // Read theme values from the latest composition, ensuring defaults
    const fontScale = currentTheme.fontScale ?? (currentTheme.fontSize ? currentTheme.fontSize / 8 : 1.0);
    const themeWithDefaults = {
      journalTemplate: currentTheme.journalTemplate,
      fontFamily: currentTheme.fontFamily || 'Arial',
      fontScale,
      // lineWidth comes from journal template (defaults to 1), not theme
      colorMode: currentTheme.colorMode || 'RGB',
      colorblindSafe: currentTheme.colorblindSafe || false,
    };
    
    console.log(`🎨 [Panel] Hydrating panel ${panel.id} with theme:`, themeWithDefaults);

    // Mark that we're starting hydration and update refs immediately to prevent duplicate hydrations
    isHydratingRef.current = true;
    lastHydratedThemeRef.current = hydrationKey;
    lastHydratedThemeKeyRef.current = actualThemeKey;
    lastHydrationSignatureRef.current = hydrationSignature;

    hydratePanel(panel, latestComposition)
      .then((result) => {
        if (!cancelled) {
          console.log(`🎨 [Panel] Panel ${panel.id} hydrated successfully`);
          setPlotlyData(result);
          setLoading(false);
          isHydratingRef.current = false;
        } else {
          // If cancelled, ensure loading state is reset
          console.log(`🎨 [Panel] Panel ${panel.id} hydration cancelled`);
          setLoading(false);
          isHydratingRef.current = false;
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('Failed to hydrate panel:', err);
          setError('Failed to load plot');
          setLoading(false);
          isHydratingRef.current = false;
          // Reset refs on error so we can retry
          lastHydratedThemeRef.current = '';
          lastHydratedThemeKeyRef.current = '';
          lastHydrationSignatureRef.current = '';
        } else {
          // If cancelled, ensure loading state is reset
          console.log(`🎨 [Panel] Panel ${panel.id} hydration cancelled (error case)`);
          setLoading(false);
          isHydratingRef.current = false;
        }
      });

    return () => {
      cancelled = true;
      // CRITICAL FIX: Reset loading state when effect is cleaned up
      // This ensures panels don't get stuck in loading state if component unmounts or effect re-runs
      setLoading(false);
      isHydratingRef.current = false;
    };
  }, [
    panel.plotId, 
    panel.plotVersionId,
    // CRITICAL: Include plotLatestVersionId so panels re-hydrate when plot is saved
    // This ensures panels that follow latest automatically pick up new versions
    plotLatestVersionId,
    // Use stable references - only re-hydrate if these actually change
    panel.layoutOverrides?.margin?.t,
    panel.layoutOverrides?.margin?.r,
    panel.layoutOverrides?.margin?.b,
    panel.layoutOverrides?.margin?.l,
    panel.layoutOverrides?.showLegend,
    panel.layoutOverrides?.axesOverride,
    panel.layoutOverrides?.zoom,
    panel.layoutOverrides?.panX,
    panel.layoutOverrides?.panY,
    panel.layoutOverrides?.hideTitle,
    panel.layoutOverrides?.hideSubtitle,
    panel.layoutOverrides?.hideAnnotations,
    // Use individual axesSharing properties directly instead of through currentComposition
    // This prevents re-renders when currentComposition object reference changes
    axesSharing?.shareXAcrossColumns,
    axesSharing?.shareXAcrossAll,
    axesSharing?.shareYAcrossRows,
    axesSharing?.warnOnScaleMismatch,
    axesSharing?.standardizeXTitles,
    axesSharing?.standardizeYTitles,
    axesSharing?.standardizeXScales,
    axesSharing?.standardizeYScales,
    themeKey, // Use the memoized theme key instead of individual properties
    // CRITICAL: Do NOT include panels in dependencies - peer layout changes should not trigger re-hydration
    // Hydration signature no longer includes peer positions, so we don't need panels dependency
  ]);

  const handleSelect = (plotId: string, plotVersionId?: string) => {
    // If plotVersionId is provided, pin to that version; otherwise follow latest (undefined)
    // When user explicitly selects a plot without a version, default to follow latest
    updatePanel(panel.id, { plotId, plotVersionId: plotVersionId ?? undefined });
    setShowPlotSelector(false);
  };

  const handleAssignClick = (e?: React.MouseEvent | React.MouseEvent<HTMLDivElement>) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
      // Also stop immediate propagation to prevent react-grid-layout from capturing
      e.nativeEvent.stopImmediatePropagation();
    }
    console.log('Opening plot selector for panel:', panel.id, 'showPlotSelector will be:', true);
    setShowPlotSelector(true);
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    // Use mousedown instead of click to fire before react-grid-layout captures the event
    if (!panel.plotId) {
      e.stopPropagation();
      e.preventDefault();
      e.nativeEvent.stopImmediatePropagation();
      console.log('MouseDown - Opening plot selector for panel:', panel.id);
      setShowPlotSelector(true);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    // Don't interfere with react-grid-layout's drag operations
    // Check if this is a library item drag (has our custom data type)
    const hasLibraryData = e.dataTransfer.types.includes('application/json');
    if (!hasLibraryData) {
      return; // Let react-grid-layout handle its own drags
    }
    
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Check if we're leaving the panel itself (not just moving to a child element)
    const currentTarget = e.currentTarget as HTMLElement;
    const relatedTarget = e.relatedTarget as HTMLElement | null;
    
    // If relatedTarget is null or not a child of currentTarget, we're leaving
    if (!relatedTarget || !currentTarget.contains(relatedTarget)) {
      setIsDragOver(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    try {
      const data = e.dataTransfer.getData('application/json');
      if (!data) return;

      const payload = JSON.parse(data);
      
      if (payload.type === 'plot' && payload.plotId) {
        // Preserve plotVersionId if present (pinned), otherwise use latest (undefined = follow latest)
        updatePanel(panel.id, { plotId: payload.plotId, plotVersionId: payload.plotVersionId });
      } else if (payload.type === 'image' && payload.imageId) {
        // For Phase 1, images are not yet fully supported in panels
        // This is a placeholder for future implementation
        console.log('Image drop not yet implemented:', payload);
      } else if (payload.type === 'template' && payload.templateId) {
        // Template drops apply to the whole figure
        const applyTemplatePreset = useFigureCompositionStore.getState().applyTemplatePreset;
        applyTemplatePreset(payload.templateId);
      }
    } catch (error) {
      console.error('Failed to handle drop:', error);
    }
  };

  if (!currentComposition) {
    return null;
  }

  const handlePanelClick = (e: React.MouseEvent) => {
    // Don't select if clicking on toolbar buttons, plot selector, or label
    const target = e.target as HTMLElement;
    if (
      target.closest('[data-panel-toolbar]') ||
      target.closest('[data-plot-selector]') ||
      target.closest('input') || // Don't select when editing label
      target.closest('[data-panel-label]') || // Don't select when clicking label
      !panel.plotId
    ) {
      return;
    }
    
    // Multi-select with Ctrl/Cmd
    if (e.ctrlKey || e.metaKey) {
      togglePanelSelection(panel.id);
    } else {
      // Single select
      setSelectedPanelId(panel.id);
    }
  };

  const handleDoubleClick = (e: React.MouseEvent) => {
    // Don't handle if clicking on toolbar or label
    const target = e.target as HTMLElement;
    if (
      target.closest('[data-panel-toolbar]') ||
      target.closest('[data-plot-selector]') ||
      target.closest('input') ||
      !panel.plotId
    ) {
      return;
    }

    // Open plot editor
    e.stopPropagation();
    if (currentComposition && panel.plotId) {
      go(routes.plotEditor.open(currentComposition.projectId, panel.plotId));
    }
  };

  const duplicatePanel = useFigureCompositionStore((state) => state.duplicatePanel);
  const removePanel = useFigureCompositionStore((state) => state.removePanel);

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Create context menu
    const menu = document.createElement('div');
    menu.className = 'fixed bg-white border border-gray-300 rounded shadow-lg py-1 z-50';
    menu.style.left = `${e.clientX}px`;
    menu.style.top = `${e.clientY}px`;

    const menuItems = [
      {
        label: 'Edit Plot',
        onClick: () => {
          if (panel.plotId && currentComposition) {
            go(routes.plotEditor.open(currentComposition.projectId, panel.plotId));
          }
          document.body.removeChild(menu);
        },
        disabled: !panel.plotId,
      },
      {
        label: 'Replace Plot',
        onClick: () => {
          setShowPlotSelector(true);
          document.body.removeChild(menu);
        },
      },
      {
        label: 'Duplicate Panel',
        onClick: () => {
          duplicatePanel(panel.id);
          document.body.removeChild(menu);
        },
      },
      { label: '---', onClick: null },
      {
        label: 'Delete Panel',
        onClick: () => {
          if (confirm('Are you sure you want to delete this panel?')) {
            removePanel(panel.id);
          }
          document.body.removeChild(menu);
        },
      },
    ];

    menuItems.forEach((item) => {
      if (item.label === '---') {
        const separator = document.createElement('div');
        separator.className = 'h-px bg-gray-200 my-1';
        menu.appendChild(separator);
      } else {
        const menuItem = document.createElement('button');
        menuItem.className = `w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
          item.disabled ? 'text-gray-400 cursor-not-allowed' : ''
        }`;
        menuItem.textContent = item.label;
        menuItem.disabled = item.disabled ?? false;
        if (item.onClick && !item.disabled) {
          menuItem.onclick = item.onClick;
        }
        menu.appendChild(menuItem);
      }
    });

    document.body.appendChild(menu);

    // Close menu when clicking outside
    const closeMenu = (e: MouseEvent) => {
      if (!menu.contains(e.target as Node)) {
        document.body.removeChild(menu);
        document.removeEventListener('click', closeMenu);
      }
    };
    setTimeout(() => document.addEventListener('click', closeMenu), 0);
  };

  // Determine base border classes - use constant border thickness to avoid layout shifts
  // Selection styling uses shadow/outline instead of changing border width
  const getBaseBorderClasses = () => {
    if (!panel.plotId) {
      return 'border-2 border-dashed border-gray-300';
    }
    if (isDragOver) {
      return 'border border-primary bg-primary/5';
    }
    // Always use constant border thickness (border = 1px) for panels with plots
    // This ensures content box size never changes
    if (showPanelBorders) {
      return 'border border-gray-300';
    }
    // Use transparent border to maintain constant thickness even when borders are hidden
    return 'border border-transparent';
  };

  // Determine selection shadow/outline - does not affect layout
  const getSelectionShadow = () => {
    if (isSelected) {
      return 'shadow-[0_0_0_2px_rgba(59,130,246,0.9)]';
    }
    if (isMultiSelected) {
      return 'shadow-[0_0_0_2px_rgba(148,163,184,0.9)]';
    }
    return '';
  };

  return (
    <>
      <div 
        ref={panelRef}
        data-panel-id={panel.id}
        className={`w-full h-full relative bg-gray-50 rounded overflow-hidden group ${getBaseBorderClasses()} ${getSelectionShadow()}`}
        style={{ boxSizing: 'border-box' }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handlePanelClick}
        onDoubleClick={handleDoubleClick}
        onContextMenu={handleContextMenu}
        role="region"
        aria-label={`Panel ${panel.label}${panel.plotId ? ' with plot' : ' (empty)'}`}
        tabIndex={0}
      >
        {shouldShowPanelLabels && <PanelLabel panel={panel} editable={!isSharedView} />}
        <PanelToolbar panel={panel} onAssignClick={handleAssignClick} />

        {!panel.plotId ? (
          <div
            className="w-full h-full flex items-center justify-center p-4 cursor-pointer hover:bg-gray-100 transition-colors"
            onClick={handleAssignClick}
            onMouseDown={handleMouseDown}
            style={{ pointerEvents: 'auto' }}
          >
            <div className="text-center" style={{ pointerEvents: 'auto' }}>
              <div className="text-sm text-gray-500 mb-1">Click to assign a plot</div>
              <div className="text-xs text-gray-400">or drag from library</div>
            </div>
          </div>
        ) : loading ? (
          <div className="w-full h-full flex items-center justify-center">
            <div className="text-xs text-gray-400">Loading...</div>
          </div>
        ) : error ? (
          <div className="w-full h-full flex items-center justify-center">
            <div className="text-xs text-destructive">{error}</div>
          </div>
        ) : plotlyData && renderedFigure ? (
          <>
            {/* For locked axes, scale proportionally to fit container while maintaining alignment */}
            {(() => {
              // Always use panel dimensions for container to prevent white space
              // The plot layout width/height should match the container exactly
              const plotWidth = panelWidthPx;
              const plotHeight = panelHeightPx;
              
              // Re-check panel context for legend preservation
              const panels = currentComposition?.panels ?? [];
              const isMultiPanelLocal = panels.length >= 2;
              const isFigureLevelModeLocal = currentComposition?.figureLegend?.enabled === true;
              const selectedLegendPanelIdLocal = currentComposition?.figureLegend?.panelId;
              const isSelectedLegendPanelLocal = isFigureLevelModeLocal
                ? (selectedLegendPanelIdLocal ? panel.id === selectedLegendPanelIdLocal : panel.id === panels[0]?.id)
                : true; // In per-panel mode, all panels are "selected"
              
              // For locked panels: use scaled layout from scaleFigureToPanel (which maintains proportional margins)
              // This locks at the current scaled proportion, preventing cropping while maintaining alignment
              // The scaled layout already has margins scaled proportionally, so we just need to ensure automargin is disabled
              const finalLayout = isAxisLocked && renderedFigure
                ? {
                    ...renderedFigure.layout,
                    autosize: false,
                    // Explicitly preserve coloraxis for heatmaps and continuous color scales
                    coloraxis: renderedFigure.layout?.coloraxis,
                    // Ensure width and height match container exactly to prevent white space
                    width: plotWidth,
                    height: plotHeight,
                    // margin values are already scaled proportionally by scaleFigureToPanel
                    margin: {
                      ...(renderedFigure.layout.margin as any),
                      autoexpand: false, // CRITICAL: Prevent Plotly from auto-adjusting margin.l per panel
                      pad: 0, // CRITICAL: Disable padding that could vary per panel
                    },
                    // CRITICAL: Explicitly disable automargin on both axes to prevent Plotly from adjusting margins
                    xaxis: {
                      ...(renderedFigure.layout.xaxis as any),
                      automargin: false, // CRITICAL: Disable x-axis automargin
                      domain: [0, 1], // CRITICAL: Ensure full domain for consistent positioning
                    },
                    yaxis: {
                      ...(renderedFigure.layout.yaxis as any),
                      automargin: false, // CRITICAL: Disable y-axis automargin
                      domain: [0, 1], // CRITICAL: Ensure full domain for consistent positioning
                    },
                    // CRITICAL: Explicitly preserve legend settings for figure-level mode (on selected panel)
                    ...(isMultiPanelLocal && isSelectedLegendPanelLocal && isFigureLevelModeLocal && legendPosition !== 'none' && renderedFigure.layout?.legend ? {
                      legend: renderedFigure.layout.legend,
                      showlegend: renderedFigure.layout.showlegend,
                    } : {}),
                  }
                : {
                    ...renderedFigure.layout,
                    autosize: false,
                    // Explicitly preserve coloraxis for heatmaps and continuous color scales
                    coloraxis: renderedFigure.layout?.coloraxis,
                    width: plotWidth,
                    height: plotHeight,
                    // CRITICAL: Explicitly preserve legend settings for figure-level mode (on selected panel)
                    ...(isMultiPanelLocal && isSelectedLegendPanelLocal && isFigureLevelModeLocal && legendPosition !== 'none' && renderedFigure.layout?.legend ? {
                      legend: renderedFigure.layout.legend,
                      showlegend: renderedFigure.layout.showlegend,
                    } : {}),
                  };
              
              // DEBUG: Log final layout before rendering - this is what we pass to <Plot /> and should match latestFigureRef
              const tracesWithColoraxis = renderedFigure.data.filter((t: any) => t.coloraxis || t.marker?.coloraxis).length;
              const finalColorscale = finalLayout.coloraxis?.colorscale ??
                (finalLayout.coloraxis && Array.isArray(finalLayout.coloraxis.colorscale)
                  ? '[array]'
                  : undefined);
              
              console.debug(`🔍 [Panel] DEBUG - finalLayout for panel ${panel.id}:`, {
                coloraxisExists: !!finalLayout.coloraxis,
                colorscale: finalColorscale,
                traceCount: renderedFigure.data.length,
                tracesWithColoraxis: tracesWithColoraxis,
                tracesWithTraceColoraxis: renderedFigure.data.filter((t: any) => t.coloraxis).length,
                tracesWithMarkerColoraxis: renderedFigure.data.filter((t: any) => t.marker?.coloraxis).length,
                traceBindings: renderedFigure.data.map((t: any) => ({
                  type: t.type,
                  traceColoraxis: t.coloraxis,
                  markerColoraxis: t.marker?.coloraxis,
                  markerColorscale: t.marker?.colorscale,
                })),
                // Sanity check: this should match latestFigureRef colorscale
                latestFigureRefColorscale: latestFigureRef.current?.layout?.coloraxis?.colorscale ??
                  (latestFigureRef.current?.layout?.coloraxis && Array.isArray(latestFigureRef.current.layout.coloraxis.colorscale)
                    ? '[array]'
                    : undefined),
                // DEBUG: Check legend settings
                showlegend: finalLayout.showlegend,
                legend: finalLayout.legend,
                margin: finalLayout.margin,
                isSelectedLegendPanel: isSelectedLegendPanelLocal,
                selectedLegendPanelId: selectedLegendPanelIdLocal,
                isFigureLevelMode: isFigureLevelModeLocal,
                legendPosition,
              });

              // Instrumentation: Log layout values for locked panels to debug geometry differences
              if (isAxisLocked) {
                console.log(`🔒 [Panel ${panel.id}] Locked panel layout:`, {
                  width: finalLayout.width,
                  height: finalLayout.height,
                  margin: finalLayout.margin,
                  xaxis: {
                    domain: (finalLayout.xaxis as any)?.domain,
                    range: (finalLayout.xaxis as any)?.range,
                  },
                  yaxis: {
                    domain: (finalLayout.yaxis as any)?.domain,
                    range: (finalLayout.yaxis as any)?.range,
                  },
                });
              }

              return (
                <>
                  {isResizing ? (
                    // PREVIEW MODE: Render cloned figure (prevents Plotly mutations from affecting other panels)
                    <div 
                      data-figure-composition-panel="true"
                      style={{
                        width: `${plotWidth}px`,
                        height: `${plotHeight}px`,
                        padding: 0,
                        margin: 0,
                        boxSizing: 'border-box',
                        display: 'block',
                        flexShrink: 0,
                        // For locked panels, ensure the container can accommodate canonical size
                        // CSS will handle overflow if container is smaller
                        overflow: isAxisLocked ? 'visible' : 'hidden',
                      }}
                    >
                      <Plot
                        data={renderedFigure.data}
                        layout={finalLayout}
                        style={{ width: `${plotWidth}px`, height: `${plotHeight}px`, padding: 0, margin: 0, boxSizing: 'border-box', display: 'block' }}
                        config={mergeMapboxConfig({ displayModeBar: false, responsive: false })}
                        onUpdate={(fig) => {
                          // Color-preserving onUpdate handler: NEVER delete or overwrite colorscale
                          // Plotly's onUpdate receives the figure after it has been rendered
                          // IMPORTANT: We consider `fig` unsafe for colorscale - it may have Plotly's internal defaults
                          // We will NOT use its data/layout to redraw or store colors
                          
                          if (!fig) return;
                          
                          // Log what Plotly is sending - this may have incorrect colorscale
                          const colorscaleFromFig =
                            fig?.layout?.coloraxis?.colorscale ??
                            (fig?.layout?.coloraxis && Array.isArray(fig.layout.coloraxis.colorscale)
                              ? '[array]'
                              : undefined);
                          
                          console.debug('🔁 [Panel] Preview onUpdate incoming fig colorscale:', {
                            panelId: panel.id,
                            colorscaleFromFig,
                            // Log what we're actually using (from ref)
                            safeColorscale: latestFigureRef.current?.layout?.coloraxis?.colorscale ??
                              (latestFigureRef.current?.layout?.coloraxis && Array.isArray(latestFigureRef.current.layout.coloraxis.colorscale)
                                ? '[array]'
                                : undefined),
                          });
                          
                          // CRITICAL: We do NOT mutate fig here
                          // We do NOT delete or overwrite marker.colorscale
                          // We do NOT overwrite layout.coloraxis.colorscale
                          // We do NOT call Plotly.newPlot with fig.data or fig.layout
                          // If manual re-render is needed, use latestFigureRef.current (color-correct figure)
                        }}
                      />
                    </div>
                  ) : (
                    // EXACT MODE: Re-render plot with cloned layout for sharp, accurate rendering
                    <div 
                      data-figure-composition-panel="true"
                      style={{
                        width: `${plotWidth}px`,
                        height: `${plotHeight}px`,
                        padding: 0,
                        margin: 0,
                        boxSizing: 'border-box',
                        display: 'block',
                        flexShrink: 0,
                        // For locked panels, ensure the container can accommodate canonical size
                        // CSS will handle overflow if container is smaller
                        overflow: isAxisLocked ? 'visible' : 'hidden',
                      }}
                    >
                      <Plot
                        data={renderedFigure.data}
                        layout={finalLayout}
                        style={{ width: `${plotWidth}px`, height: `${plotHeight}px`, padding: 0, margin: 0, boxSizing: 'border-box', display: 'block' }}
                        config={mergeMapboxConfig({ displayModeBar: false, responsive: false })}
                        onUpdate={(fig) => {
                          // Color-preserving onUpdate handler: NEVER delete or overwrite colorscale
                          // Plotly's onUpdate receives the figure after it has been rendered
                          // IMPORTANT: We consider `fig` unsafe for colorscale - it may have Plotly's internal defaults
                          // We will NOT use its data/layout to redraw or store colors
                          
                          if (!fig) return;
                          
                          // Log what Plotly is sending - this may have incorrect colorscale
                          const colorscaleFromFig =
                            fig?.layout?.coloraxis?.colorscale ??
                            (fig?.layout?.coloraxis && Array.isArray(fig.layout.coloraxis.colorscale)
                              ? '[array]'
                              : undefined);
                          
                          console.debug('🔁 [Panel] Exact onUpdate incoming fig colorscale:', {
                            panelId: panel.id,
                            colorscaleFromFig,
                            // Log what we're actually using (from ref)
                            safeColorscale: latestFigureRef.current?.layout?.coloraxis?.colorscale ??
                              (latestFigureRef.current?.layout?.coloraxis && Array.isArray(latestFigureRef.current.layout.coloraxis.colorscale)
                                ? '[array]'
                                : undefined),
                          });
                          
                          // CRITICAL: We do NOT mutate fig here
                          // We do NOT delete or overwrite marker.colorscale
                          // We do NOT overwrite layout.coloraxis.colorscale
                          // We do NOT call Plotly.newPlot with fig.data or fig.layout
                          // If manual re-render is needed, use latestFigureRef.current (color-correct figure)
                          
                          // If we need to force a re-render (e.g., for exact mode), use the color-correct figure from ref
                          // Example (currently not needed, but if required):
                          // if (panelRef.current && latestFigureRef.current) {
                          //   const plotElement = panelRef.current.querySelector('.js-plotly-plot') as any;
                          //   if (plotElement && window.Plotly) {
                          //     const { data, layout } = latestFigureRef.current;
                          //     console.debug('🎯 [Panel] Exact onUpdate re-render using safe figure colorscale:', {
                          //       safeColorscale: layout?.coloraxis?.colorscale ?? (Array.isArray(layout?.coloraxis?.colorscale) ? '[array]' : undefined),
                          //     });
                          //     window.Plotly.newPlot(plotElement, data, layout, { displayModeBar: false, responsive: false });
                          //   }
                          // }
                        }}
                      />
                    </div>
                  )}
                </>
              );
            })()}
            {/* Annotation Layer - renders on top of plot */}
            {panelDimensions.width > 0 && panelDimensions.height > 0 && (
              <AnnotationLayer
                panel={panel}
                panelWidth={panelDimensions.width}
                panelHeight={panelDimensions.height}
                theme={currentComposition.theme}
              />
            )}
          </>
        ) : null}
      </div>

      <PlotSelector
        projectId={currentComposition.projectId}
        isOpen={showPlotSelector}
        onSelect={handleSelect}
        onClose={() => {
          console.log('Closing plot selector');
          setShowPlotSelector(false);
        }}
      />
    </>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function for memoization
  // Only re-render if panel properties actually change
  return (
    prevProps.panel.id === nextProps.panel.id &&
    prevProps.panel.plotId === nextProps.panel.plotId &&
    prevProps.panel.plotVersionId === nextProps.panel.plotVersionId &&
    prevProps.panel.x === nextProps.panel.x &&
    prevProps.panel.y === nextProps.panel.y &&
    prevProps.panel.w === nextProps.panel.w &&
    prevProps.panel.h === nextProps.panel.h &&
    prevProps.panel.panelWidthMm === nextProps.panel.panelWidthMm &&
    prevProps.panel.panelHeightMm === nextProps.panel.panelHeightMm &&
    prevProps.panel.label === nextProps.panel.label &&
    prevProps.panel.locked === nextProps.panel.locked &&
    prevProps.isResizing === nextProps.isResizing &&
    prevProps.isReadOnly === nextProps.isReadOnly &&
    JSON.stringify(prevProps.panel.layoutOverrides) === JSON.stringify(nextProps.panel.layoutOverrides) &&
    JSON.stringify(prevProps.panel.annotations) === JSON.stringify(nextProps.panel.annotations) &&
    JSON.stringify(prevProps.panel.labelStyle) === JSON.stringify(nextProps.panel.labelStyle)
  );
});
